import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  Box,
  Flex,
  Text,
  VStack,
  SkeletonText,
  Skeleton,
  Spacer,
} from '@chakra-ui/react';

import { CheckCircleIcon } from '@chakra-ui/icons';

import { getProgressionPercentage } from 'store/modules/progression/progression-slice';

import { ReducerStatus } from 'utils/constants';

import AccessTag from 'components/common/fields/AccessTag';
import IdTag from 'components/common/fields/IdTag';
import TagGroup from 'components/common/fields/TagGroup';
import EmailGroup from 'components/common/fields/EmailGroup';
import PublishButton from 'components/common/buttons/PublishButton';
import CourseImage from './CourseImage';
import ProgressDisplay from './ProgressDisplay';

function CourseHeader({ course, isLoading, isAdminMode, ...rest }) {
  const getProgressionStatus = useSelector(
    (state) => state.progression.getProgressionStatus
  );

  const subQuestCount = useSelector(
    (state) => state.progression.totalSubQuestCount
  );

  const progressionPercentage = useSelector(getProgressionPercentage);

  return (
    <Box {...rest}>
      {' '}
      <Flex direction={{ base: 'column', sm: 'row' }}>
        <Skeleton
          h={{ base: '100%', sm: '200px' }}
          w={{ base: '100%', sm: '300px' }}
          isLoaded={!isLoading}
        >
          <Box
            h={{ base: '100%', sm: '200px' }}
            w={{ base: '100%', sm: '300px' }}
          >
            <CourseImage
              borderRadius="10"
              name={course?.name}
              image={course?.thumbnail}
              category={course?.category}
            />
          </Box>
        </Skeleton>

        <Box px={{ base: 0, sm: 5 }} mt={{ base: 5, sm: 0 }} flex="1">
          <VStack spacing={1} align="start">
            <SkeletonText noOfLines={2} width="100%" isLoaded={!isLoading}>
              <Flex gap={2} align="center">
                <IdTag id={course?.id} />
                <Text flex={1} fontWeight={500} fontSize="xl">
                  {course?.name}

                  {progressionPercentage === 100 && (
                    <CheckCircleIcon ml={2} color="green" boxSize={4} />
                  )}
                </Text>
              </Flex>

              {isAdminMode && (
                <Text mt={1} fontSize="xs" fontWeight={550}>
                  Owner: {course?.owner?.name || 'N/A'}
                </Text>
              )}
            </SkeletonText>

            <SkeletonText noOfLines={4} isLoaded={!isLoading}>
              <Text fontSize="lg">{course?.description}</Text>
            </SkeletonText>

            <Box>
              <SkeletonText noOfLines={4} isLoaded={!isLoading}>
                <Box>
                  <TagGroup tags={course?.tags} />
                </Box>

                <Flex gap={2}>
                  {isAdminMode && (
                    <PublishButton
                      entityType="Course"
                      entityId={course?.id}
                      isPublished={course?.isPublished}
                    />
                  )}
                  {isAdminMode && <AccessTag isPrivate={course?.isPrivate} />}
                </Flex>
              </SkeletonText>
            </Box>
            <Spacer />
            <Skeleton height="10px" width="100%" isLoaded={!isLoading}>
              {!isAdminMode && (
                <ProgressDisplay
                  percentage={progressionPercentage}
                  count={subQuestCount}
                  isLoading={getProgressionStatus === ReducerStatus.LOADING}
                />
              )}
            </Skeleton>
          </VStack>
        </Box>
      </Flex>
      {isAdminMode && (
        <EmailGroup mt={4} name="Contributors: " emails={course?.ownerGroups} />
      )}
      {isAdminMode && (
        <EmailGroup mt={4} name="Access List: " emails={course?.targetGroups} />
      )}
    </Box>
  );
}

CourseHeader.defaultProps = {
  course: null,
  isAdminMode: false,
};

CourseHeader.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  course: PropTypes.object,
  isAdminMode: PropTypes.bool,
};

export default CourseHeader;
